"use client";

import type { ReactNode } from "react";
import { cn } from "../utils";

export interface FilterChipsProps<T extends string> {
	/** The selectable values, in display order. */
	options: readonly T[];
	value: T;
	onChange: (value: T) => void;
	/** Chip label; defaults to the raw value with underscores as spaces. */
	renderLabel?: (option: T) => ReactNode;
	label?: string;
	className?: string;
	disabled?: boolean;
}

function humanize(option: string): string {
	return option.replaceAll("_", " ");
}

/**
 * Single-select chip row for list filters. Generic over the filter union so a
 * page cannot hand it a value outside its own filter type.
 */
export function FilterChips<T extends string>({
	options,
	value,
	onChange,
	renderLabel = humanize,
	label,
	className,
	disabled,
}: FilterChipsProps<T>) {
	return (
		// biome-ignore lint/a11y/useSemanticElements: a radiogroup of buttons, not a form fieldset
		<div
			role="radiogroup"
			aria-label={label}
			className={cn("flex flex-wrap items-center gap-1", className)}
		>
			{options.map((option) => {
				const selected = option === value;
				return (
					<button
						key={option}
						type="button"
						role="radio"
						aria-checked={selected}
						disabled={disabled}
						onClick={() => onChange(option)}
						className={cn(
							"rounded-full px-4 py-1.5 text-sm transition-colors disabled:pointer-events-none disabled:opacity-50",
							selected
								? "bg-primary font-medium text-primary-foreground"
								: "text-muted-foreground hover:bg-muted hover:text-foreground",
						)}
					>
						{renderLabel(option)}
					</button>
				);
			})}
		</div>
	);
}
